import {
  menu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import type {
  DatabaseAllViewEvents,
  EventTraceFn,
} from '@blocksuite/affine-shared/services';
import { html } from 'lit';

import { filterTraitKey } from '../../../core/filter/trait.js';
import { dataViewI18nT } from '../../../core/i18n.js';
import { DataViewSearchCollaboration } from '../../../core/search-collaboration.js';
import { sortTraitKey } from '../../../core/sort/manager.js';
import { createSortUtils } from '../../../core/sort/utils.js';
import { popFilterRoot } from '../../../widget-presets/quick-setting-bar/filter/root-panel-view.js';
import { popSortRoot } from '../../../widget-presets/quick-setting-bar/sort/root-panel.js';
import { animateCalendarSourcePopup } from './calendar-chrome-motion.js';
import {
  CalendarCommandError,
  type CalendarSourceCommandSession,
} from './calendar-command-session.js';

/** Both hosts use the existing source traits, popup editors and search collaboration contract. */
export function openCalendarSourceTool(
  session: CalendarSourceCommandSession,
  tool: 'filter' | 'sort' | 'search',
  anchor: HTMLElement,
  eventTrace: EventTraceFn<DatabaseAllViewEvents>
) {
  const view = session.view;
  if (tool !== 'search' && view.readonly$.value)
    throw new CalendarCommandError('readonly', 'calendar.chrome.readonly');
  const target = popupTargetFromElement(anchor);
  if (tool === 'filter') {
    const filterTrait = view.traitGet(filterTraitKey);
    if (!filterTrait)
      throw new CalendarCommandError(
        'unavailable',
        'calendar.chrome.unavailable'
      );
    const popup = popFilterRoot(target, {
      filterTrait,
      dataViewLogic: { eventTrace },
      title: `${view.name$.value} · ${dataViewI18nT('calendar.chrome.filter')}`,
      onBack: () => popup.close(),
      onClose: () => popup.close(),
    });
    return animateCalendarSourcePopup(popup);
  }
  if (tool === 'sort') {
    const sortTrait = view.traitGet(sortTraitKey);
    if (!sortTrait)
      throw new CalendarCommandError(
        'unavailable',
        'calendar.chrome.unavailable'
      );
    return animateCalendarSourcePopup(
      popSortRoot(target, {
        sortUtils: createSortUtils(sortTrait, eventTrace),
        title: {
          text: `${view.name$.value} · ${dataViewI18nT('calendar.chrome.sort')}`,
        },
      })
    );
  }
  const popup = popMenu(target, {
    options: {
      title: {
        text: `${view.name$.value} · ${dataViewI18nT('calendar.chrome.search')}`,
      },
      items: [
        menu.group({
          items: [
            () =>
              html`<form
                @submit=${(event: SubmitEvent) => {
                  event.preventDefault();
                  const input = (
                    event.currentTarget as HTMLFormElement
                  ).querySelector<HTMLInputElement>('input')!;
                  const query = input.value.trim();
                  view
                    .serviceGet(DataViewSearchCollaboration)
                    ?.update(view.id, {
                      expanded: Boolean(query),
                      input: query,
                      query,
                    });
                  view.setSearch(query);
                  popup.close();
                }}
              >
                <input
                  type="search"
                  aria-label=${dataViewI18nT('calendar.chrome.search')}
                  .value=${view.search$.value}
                /><button type="submit">
                  ${dataViewI18nT('calendar.chrome.search')}
                </button>
              </form>`,
          ],
        }),
      ],
    },
  });
  return animateCalendarSourcePopup(popup);
}

export function openCalendarSourceRename(
  session: CalendarSourceCommandSession,
  anchor: HTMLElement,
  onRename: (title: string) => Promise<void>,
  onError: (error: unknown) => void
) {
  const popup = popMenu(popupTargetFromElement(anchor), {
    options: {
      title: {
        text: `${session.view.name$.value} · ${dataViewI18nT('calendar.chrome.renameSource')}`,
      },
      items: [
        () =>
          html`<form
            @submit=${(event: SubmitEvent) => {
              event.preventDefault();
              const title = (event.currentTarget as HTMLFormElement)
                .querySelector<HTMLInputElement>('input')!
                .value.trim();
              if (!title) return;
              if (session.view.readonly$.value) {
                onError(
                  new CalendarCommandError(
                    'readonly',
                    'calendar.chrome.readonly'
                  )
                );
                return;
              }
              void onRename(title)
                .then(() => popup.close())
                .catch(onError);
            }}
          >
            <input
              aria-label=${dataViewI18nT('calendar.chrome.renameSource')}
              .value=${session.view.name$.value}
            /><button type="submit">
              ${dataViewI18nT('calendar.chrome.renameSource')}
            </button>
          </form>`,
      ],
    },
  });
  return animateCalendarSourcePopup(popup);
}

export function openCalendarSourceViews(
  session: CalendarSourceCommandSession,
  anchor: HTMLElement,
  onOpen: (viewId: string) => void
) {
  return animateCalendarSourcePopup(
    popMenu(popupTargetFromElement(anchor), {
      options: {
        title: {
          text: `${session.view.name$.value} · ${dataViewI18nT('calendar.chrome.sourceViews')}`,
        },
        items: session.view.manager.views$.value.flatMap(id => {
          const data = session.view.manager.viewDataGet(id);
          return data
            ? [menu.action({ name: data.name, select: () => onOpen(id) })]
            : [];
        }),
      },
    })
  );
}
